import React, { useRef, useState } from "react";

import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Eye,
  Film,
  Loader2,
  RefreshCw,
  Scissors,
  Sparkles,
} from "lucide-react";

import {
  downloadClip,
  getClipDownloadURL,
  getClipPreviewURL,
  getClipThumbnailURL,
} from "./clipApi";

/*
 * --------------------------------------------------------
 * Progress
 * --------------------------------------------------------
 */

function clampProgress(value) {
  const progress = Number(value);

  if (!Number.isFinite(progress)) {
    return 0;
  }

  return Math.min(100, Math.max(0, Math.round(progress)));
}

/*
 * --------------------------------------------------------
 * Time formatting
 * --------------------------------------------------------
 */

function formatTime(seconds) {
  const value = Number(seconds);

  if (!Number.isFinite(value) || value < 0) {
    return "0:00";
  }

  const wholeSeconds = Math.floor(value);

  const minutes = Math.floor(wholeSeconds / 60);

  const remainingSeconds = wholeSeconds % 60;

  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

function getClipDuration(clip) {
  const explicitDuration = Number(clip?.duration_seconds);

  if (Number.isFinite(explicitDuration) && explicitDuration > 0) {
    return explicitDuration;
  }

  const start = Number(clip?.start_seconds ?? clip?.start_sec ?? 0);

  const end = Number(clip?.end_seconds ?? clip?.end_sec ?? 0);

  return Math.max(0, end - start);
}

/*
 * --------------------------------------------------------
 * Processing stages
 * --------------------------------------------------------
 */

function getProcessingStage(progress, queued) {
  if (queued) {
    return {
      title: "Waiting for processing to start",

      description: "Your analysis is queued and will start automatically.",
    };
  }

  if (progress < 25) {
    return {
      title: "Preparing your video",

      description: "Downloading and validating the source video.",
    };
  }

  if (progress < 35) {
    return {
      title: "Preparing the audio",

      description: "Extracting clean speech audio for transcription.",
    };
  }

  if (progress < 60) {
    return {
      title: "Transcribing the video",

      description: "Turning speech into a timestamped transcript.",
    };
  }

  if (progress < 75) {
    return {
      title: "Finding the strongest moments",

      description: "Selecting useful, engaging clips from the transcript.",
    };
  }

  if (progress < 90) {
    return {
      title: "Generating clip previews",

      description: "Rendering vertical previews, captions and thumbnails.",
    };
  }

  return {
    title: "Finalizing your clips",

    description: "Uploading assets and saving the final clip results.",
  };
}

/*
 * --------------------------------------------------------
 * Video thumbnail fallback
 * --------------------------------------------------------
 *
 * If the backend does not provide a thumbnail image,
 * use the actual preview video as the visual thumbnail.
 *
 * We seek slightly into the video so the browser displays
 * a real frame instead of a black first frame.
 * --------------------------------------------------------
 */

function VideoThumbnail({ src, title }) {
  const videoRef = useRef(null);

  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-950 via-gray-900 to-indigo-950">
        <Scissors className="h-9 w-9 text-white/35" aria-hidden="true" />
      </div>
    );
  }

  const seekToPreviewFrame = (event) => {
    const video = event.currentTarget;

    if (!Number.isFinite(video.duration) || video.duration <= 0) {
      return;
    }

    try {
      video.currentTime = Math.min(0.2, Math.max(0, video.duration / 20));
    } catch {
      // Browser can still display its default video frame.
    }
  };

  return (
    <video
      ref={videoRef}
      src={src}
      muted
      playsInline
      preload="metadata"
      aria-label={title || "Clip thumbnail"}
      onLoadedMetadata={seekToPreviewFrame}
      onError={() => setFailed(true)}
      className="h-full w-full object-cover"
    />
  );
}

/*
 * --------------------------------------------------------
 * Clip card
 * --------------------------------------------------------
 */

function ClipResultCard({ clip, onPreview, onAdjust, retrimState }) {
  const previewURL = getClipPreviewURL(clip);

  const thumbnailURL = getClipThumbnailURL(clip);

  const downloadURL = getClipDownloadURL(clip);

  const [thumbnailFailed, setThumbnailFailed] = useState(false);

  const [downloadError, setDownloadError] = useState("");

  const start = Number(clip?.start_seconds ?? clip?.start_sec ?? 0);

  const end = Number(clip?.end_seconds ?? clip?.end_sec ?? 0);

  const duration = getClipDuration(clip);

  const score = Number(clip?.score_total ?? clip?.score ?? 0);

  const rank = Number(clip?.rank || clip?.clip_index || 0);

  const suggestionId = String(clip?.id || clip?.clip_id || "").trim();

  const adjustingThisClip =
    Boolean(suggestionId) &&
    retrimState?.suggestionId === suggestionId &&
    (retrimState?.status === "queued" || retrimState?.status === "running");

  /*
   * ------------------------------------------------------
   * Download
   * ------------------------------------------------------
   */

  const handleDownload = () => {
    if (!downloadURL) {
      return;
    }

    setDownloadError("");

    try {
      downloadClip(clip);
    } catch (error) {
      console.error("clip_download_failed", error);

      setDownloadError(error?.message || "The clip could not be downloaded.");
    }
  };

  /*
   * ------------------------------------------------------
   * Thumbnail
   * ------------------------------------------------------
   *
   * Priority:
   *
   * 1. Backend thumbnail URL
   * 2. Preview video frame
   * 3. Placeholder
   */

  const renderThumbnail = () => {
    if (thumbnailURL && !thumbnailFailed) {
      return (
        <img
          src={thumbnailURL}
          alt={`Thumbnail for ${clip?.title || `clip ${rank}`}`}
          loading="lazy"
          className="h-full w-full object-cover"
          onError={() => setThumbnailFailed(true)}
        />
      );
    }

    return <VideoThumbnail src={previewURL} title={clip?.title} />;
  };

  return (
    <article className="flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:border-indigo-200 hover:shadow-md">
      {/*
       * --------------------------------------------------
       * Compact vertical preview
       *
       * The old card let the 9:16 area stretch to almost
       * the full width of a 2-column grid.
       *
       * We intentionally cap the vertical media width.
       * --------------------------------------------------
       */}

      <div className="bg-gray-950 p-3">
        <div className="relative mx-auto aspect-[9/16] w-full max-w-[210px] overflow-hidden rounded-lg bg-black">
          {renderThumbnail()}

          {/* Overlay */}

          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/25" />

          {/* Clip + score */}

          <div className="absolute left-2.5 top-2.5 flex flex-wrap items-center gap-1.5">
            <span className="rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">
              Clip {rank || "—"}
            </span>

            {score > 0 && (
              <span className="rounded-full bg-emerald-500/90 px-2.5 py-1 text-[11px] font-bold text-white backdrop-blur-sm">
                {Math.round(score)} score
              </span>
            )}
          </div>

          {/* Time */}

          <div className="absolute inset-x-2.5 bottom-2.5 flex items-end justify-between gap-2">
            <span className="rounded-md bg-black/70 px-2 py-1 text-[11px] font-medium text-white backdrop-blur-sm">
              {formatTime(start)}–{formatTime(end)}
              {" · "}
              {Math.round(duration)}s
            </span>

            <button
              type="button"
              onClick={() => onPreview(clip)}
              disabled={!previewURL || adjustingThisClip}
              aria-label={`Preview ${clip?.title || `clip ${rank}`}`}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-gray-950 shadow-lg transition hover:scale-105 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Eye className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/*
       * --------------------------------------------------
       * Clip information
       * --------------------------------------------------
       */}

      <div className="flex flex-1 flex-col p-4">
        <div>
          <h3 className="line-clamp-2 text-sm font-semibold leading-5 text-gray-950">
            {clip?.title || "Untitled clip"}
          </h3>

          {(clip?.hook || clip?.subtitle) && (
            <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-gray-600">
              {clip?.hook || clip?.subtitle}
            </p>
          )}
        </div>

        {downloadError && (
          <p role="alert" className="mt-3 text-xs leading-5 text-red-600">
            {downloadError}
          </p>
        )}

        {/*
         * ------------------------------------------------
         * Actions
         * ------------------------------------------------
         */}

        <div className="mt-auto grid grid-cols-2 gap-2 pt-4">
          <button
            type="button"
            onClick={() => onPreview(clip)}
            disabled={!previewURL || adjustingThisClip}
            className="col-span-2 inline-flex min-w-0 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2.5 text-xs font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Eye className="h-4 w-4 shrink-0" />
            Watch clip
          </button>

          <button
            type="button"
            onClick={() => onAdjust?.(clip)}
            disabled={!suggestionId || adjustingThisClip}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-indigo-200 px-3 py-2.5 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {adjustingThisClip ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Scissors className="h-4 w-4" />
            )}

            {adjustingThisClip
              ? `${clampProgress(retrimState?.progress)}%`
              : "Adjust"}
          </button>

          <button
            type="button"
            onClick={handleDownload}
            disabled={!downloadURL || adjustingThisClip}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 px-3 py-2.5 text-xs font-semibold text-gray-700 transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Download className="h-4 w-4" />
            Download
          </button>
        </div>

        {adjustingThisClip && (
          <p className="mt-2 text-center text-[11px] font-medium text-indigo-600">
            {retrimState?.message || "Generating adjusted clip…"}
          </p>
        )}

        {clip?.is_adjusted && (
          <p className="mt-2 text-center text-[11px] font-medium text-emerald-600">
            Adjusted clip
          </p>
        )}
      </div>
    </article>
  );
}

/*
 * --------------------------------------------------------
 * Panel header
 * --------------------------------------------------------
 */

function PanelHeader({ job, statusLabel, statusStyle }) {
  return (
    <div className="mb-6 flex flex-col justify-between gap-3 border-b border-gray-100 pb-5 sm:flex-row sm:items-start">
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
          Video {job.video_id || "analysis"}
        </p>

        <h2 className="mt-1 truncate text-lg font-semibold text-gray-950">
          {job.title ||
            job.file_name ||
            `Analysis job ${String(job.id).slice(0, 8)}`}
        </h2>
      </div>

      <span
        className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${statusStyle}`}
      >
        {statusLabel}
      </span>
    </div>
  );
}

/*
 * --------------------------------------------------------
 * JobDetailsPanel
 * --------------------------------------------------------
 */

export default function JobDetailsPanel({
  selectedJob,
  clips = [],
  clipsLoading,
  handleRetry,
  handleRefreshResults,
  renderTimeline,
  getJobStateUI,
  setPreviewClip,
  onAdjustClip,
  retrimState,
}) {
  const [retrying, setRetrying] = useState(false);

  const handleRetryClick = async () => {
    if (!selectedJob?.video_id || retrying) {
      return;
    }

    setRetrying(true);

    try {
      await handleRetry?.();
    } catch (error) {
      // CreateClips normally handles API errors/toasts itself.
      // Keep this catch as a final UI safeguard.
      console.error("analysis_retry_failed", error);
    } finally {
      setRetrying(false);
    }
  };

  /*
   * ------------------------------------------------------
   * No job selected
   * ------------------------------------------------------
   */

  if (!selectedJob) {
    return (
      <div className="flex min-h-[360px] flex-col items-center justify-center px-6 text-center">
        <span className="rounded-full bg-indigo-50 p-4 text-indigo-500">
          <Film className="h-8 w-8" aria-hidden="true" />
        </span>

        <h2 className="mt-4 text-sm font-semibold text-gray-900">
          Select an analysis
        </h2>

        <p className="mt-1 max-w-xs text-sm leading-6 text-gray-500">
          Choose a job from the list to view its progress or generated clips.
        </p>
      </div>
    );
  }

  const state = getJobStateUI(selectedJob);

  const progress = clampProgress(
    selectedJob.display_progress ?? selectedJob.progress,
  );

  /*
   * ------------------------------------------------------
   * Failed
   * ------------------------------------------------------
   */

  if (state === "failed") {
    return (
      <div>
        <PanelHeader
          job={selectedJob}
          statusLabel="Failed"
          statusStyle="bg-red-50 text-red-700 ring-red-200"
        />

        <div className="rounded-xl border border-red-200 bg-red-50 p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />

            <div>
              <h3 className="font-semibold text-red-950">
                We couldn’t finish this analysis
              </h3>

              <p className="mt-1 text-sm leading-6 text-red-700">
                {selectedJob.message ||
                  selectedJob.error ||
                  "An unexpected processing error occurred."}
              </p>

              {selectedJob.last_failed_stage && (
                <p className="mt-2 text-xs font-medium text-red-600">
                  Failed stage: {selectedJob.last_failed_stage}
                </p>
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={handleRetryClick}
          disabled={!selectedJob.video_id || retrying}
          aria-busy={retrying}
          className="mt-5 inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {retrying ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}

          {retrying ? "Retrying…" : "Retry analysis"}
        </button>
      </div>
    );
  }

  /*
   * ------------------------------------------------------
   * Processing
   * ------------------------------------------------------
   */

  if (state === "processing") {
    const queued = selectedJob.status === "queued";

    const processingStage = getProcessingStage(progress, queued);

    return (
      <div>
        <PanelHeader
          job={selectedJob}
          statusLabel={queued ? "Queued" : "Processing"}
          statusStyle={
            queued
              ? "bg-amber-50 text-amber-700 ring-amber-200"
              : "bg-indigo-50 text-indigo-700 ring-indigo-200"
          }
        />

        <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-5 sm:p-6">
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-indigo-100 p-2 text-indigo-600">
              {queued ? (
                <Sparkles className="h-5 w-5 animate-pulse" />
              ) : (
                <Loader2 className="h-5 w-5 animate-spin" />
              )}
            </span>

            <div className="min-w-0">
              <h3 className="font-semibold text-gray-950">
                {processingStage.title}
              </h3>

              <p className="mt-0.5 text-sm text-gray-600">
                {processingStage.description}
              </p>

              {selectedJob.message && (
                <p className="mt-2 text-xs font-semibold text-indigo-700">
                  {selectedJob.message}
                </p>
              )}
            </div>
          </div>

          <div className="mt-6">
            <div className="mb-2 flex items-center justify-between text-xs font-medium text-gray-500">
              <span>Processing progress</span>

              <span>{progress}%</span>
            </div>

            <div
              className="h-2.5 overflow-hidden rounded-full bg-white ring-1 ring-indigo-100"
              role="progressbar"
              aria-label="Analysis processing progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progress}
            >
              <div
                className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-600 transition-[width] duration-700"
                style={{
                  width: `${progress}%`,
                }}
              />
            </div>
          </div>
        </div>

        {renderTimeline?.()}
      </div>
    );
  }

  /*
   * ------------------------------------------------------
   * Completed
   * ------------------------------------------------------
   */

  if (state === "completed") {
    const sortedClips = [...clips].sort(
      (left, right) => Number(left.rank || 0) - Number(right.rank || 0),
    );

    return (
      <div>
        <PanelHeader
          job={selectedJob}
          statusLabel="Ready"
          statusStyle="bg-emerald-50 text-emerald-700 ring-emerald-200"
        />

        <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />

              <h3 className="font-semibold text-gray-950">Generated clips</h3>
            </div>

            <p className="mt-1 text-sm text-gray-500">
              Review and download the strongest moments selected from your
              video.
            </p>
          </div>

          <button
            type="button"
            onClick={handleRefreshResults}
            disabled={clipsLoading}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw
              className={`h-4 w-4 ${clipsLoading ? "animate-spin" : ""}`}
            />
            Refresh links
          </button>
        </div>

        {/*
         * ------------------------------------------------
         * Loading
         *
         * Important:
         * xl:grid-cols-3 rather than 2xl:grid-cols-3.
         * ------------------------------------------------
         */}

        {clipsLoading ? (
          <div className="grid items-stretch gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="overflow-hidden rounded-xl border border-gray-200 bg-white"
              >
                <div className="bg-gray-950 p-3">
                  <div className="mx-auto aspect-[9/16] w-full max-w-[210px] animate-pulse rounded-lg bg-gray-800" />
                </div>

                <div className="space-y-3 p-4">
                  <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />

                  <div className="h-9 animate-pulse rounded bg-gray-100" />
                </div>
              </div>
            ))}
          </div>
        ) : sortedClips.length > 0 ? (
          /*
           * ------------------------------------------------
           * Results
           *
           * Three columns begin at XL instead of 2XL.
           * ------------------------------------------------
           */

          <div className="grid items-stretch gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {sortedClips.map((clip) => (
              <ClipResultCard
                key={clip.id || clip.rank}
                clip={clip}
                onPreview={setPreviewClip}
                onAdjust={onAdjustClip}
                retrimState={retrimState}
              />
            ))}
          </div>
        ) : (
          /*
           * ------------------------------------------------
           * No clip assets yet
           * ------------------------------------------------
           */

          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center">
            <Scissors className="mx-auto h-8 w-8 text-gray-300" />

            <h3 className="mt-3 text-sm font-semibold text-gray-800">
              Clip results are not available yet
            </h3>

            <p className="mx-auto mt-1 max-w-sm text-sm leading-6 text-gray-500">
              The analysis completed, but its preview assets may still be
              loading or the signed links may have expired.
            </p>

            <button
              type="button"
              onClick={handleRefreshResults}
              disabled={clipsLoading}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${clipsLoading ? "animate-spin" : ""}`}
              />
              Load clips
            </button>
          </div>
        )}
      </div>
    );
  }

  return null;
}
